import { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'

import GeneratePaymentButton from './CellAction/components/GeneratePaymentButton'
import ViewSummaryButton from './CellAction/components/ViewSummaryButton'
import ConfirmTransactionButton from './CellAction/components/ConfirmTransactionButton'
import { formatBillingMonth } from '../BillingDetailsSummary/constants'

import type {
	CryptoInvoiceData,
	CryptoInvoiceType,
	InvoiceStatus,
} from '../../type'

const getStatusText = (status: InvoiceStatus): string => {
	const statusMap: Record<InvoiceStatus, string> = {
		CREATED: 'สร้างแล้ว',
		PENDING: 'รอดำเนินการ',
		PROCESSING: 'กำลังดำเนินการ',
		COMPLETED: 'ยืนยันแล้ว',
		MANUAL: 'สร้างโดยแอดมิน',
		FAILED: 'ล้มเหลว',
		EXPIRED: 'หมดอายุ',
		CANCELED: 'ยกเลิก',
	}
	return statusMap[status] || status
}

const getStatusColor = (status: InvoiceStatus): string => {
	const statusColors: Record<InvoiceStatus, string> = {
		CREATED: 'bg-gray-100 text-gray-800',
		PENDING: 'bg-yellow-100 text-yellow-800',
		PROCESSING: 'bg-blue-100 text-blue-800',
		COMPLETED: 'bg-green-100 text-green-800',
		MANUAL: 'bg-purple-100 text-purple-800',
		FAILED: 'bg-red-100 text-red-800',
		EXPIRED: 'bg-gray-100 text-gray-800',
		CANCELED: 'bg-gray-100 text-gray-800',
	}
	return statusColors[status] || 'bg-gray-100 text-gray-800'
}

export const columns: ColumnDef<CryptoInvoiceData>[] = [
	{
		header: 'Transaction ID',
		accessorKey: 'txId',
		cell: ({ row }) => (
			<span className="font-mono text-sm">{row.original.txId}</span>
		),
	},
	{
		header: 'ประเภท',
		accessorKey: 'invoiceType',
		cell: ({ row }) => {
			const invoiceType = row.original.invoiceType
			if (!invoiceType) {
				return <span className="text-xs text-muted-foreground">-</span>
			}
			const typeMap: Record<
				CryptoInvoiceType,
				{ label: string; color: string }
			> = {
				MANUAL: {
					label: 'สร้างโดยแอดมิน',
					color: 'bg-purple-100 text-purple-800',
				},
				MONTHLY_FEE: {
					label: 'ค่ารายเดือน',
					color: 'bg-blue-100 text-blue-800',
				},
				COMPANY_COMMISSION: {
					label: 'ค่าเครดิต',
					color: 'bg-green-100 text-green-800',
				},
				PREPAYMENT: {
					label: 'ชำระล่วงหน้า',
					color: 'bg-amber-100 text-amber-800',
				},
			}
			const typeInfo = typeMap[invoiceType as CryptoInvoiceType]
			if (!typeInfo) {
				return <span className="text-xs text-muted-foreground">-</span>
			}
			return (
				<span
					className={`inline-block whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ${typeInfo.color}`}
				>
					{typeInfo.label}
				</span>
			)
		},
	},
	{
		header: 'จำนวนเงิน (USD)',
		accessorKey: 'amount',
		cell: ({ row }) => (
			<span className="font-semibold">
				$
				{row.original.amount.toLocaleString('th-TH', {
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				})}
			</span>
		),
	},
	{
		header: 'เครือข่าย',
		accessorKey: 'chain',
		cell: ({ row }) => (
			<span className="rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
				{row.original.chain || '-'}
			</span>
		),
	},
	{
		header: 'Wallet Address',
		accessorKey: 'walletAddress',
		cell: ({ row }) => {
			const address = row.original.walletAddress
			if (!address) return <span className="text-muted-foreground">-</span>
			return (
				<span className="font-mono text-xs" title={address}>
					{address.slice(0, 6)}...{address.slice(-4)}
				</span>
			)
		},
	},
	{
		header: 'สถานะ',
		accessorKey: 'status',
		cell: ({ row }) => {
			const status = row.original.status
			return (
				<span
					className={`inline-block whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(status)}`}
				>
					{getStatusText(status)}
				</span>
			)
		},
	},
	{
		header: 'เดือนบิล',
		accessorKey: 'billingMonth',
		cell: ({ row }) =>
			row.original.billingMonth
				? formatBillingMonth(row.original.billingMonth)
				: '-',
	},
	{
		header: 'วันที่สร้าง',
		accessorKey: 'createdAt',
		cell: ({ row }) =>
			format(new Date(row.original.createdAt), 'dd MMM yyyy HH:mm', {
				locale: th,
			}),
	},

	{
		header: 'จัดการ',
		accessorKey: 'actions',
		cell: ({ row }) => (
			<div className="flex items-center gap-2">
				<ViewSummaryButton invoice={row.original} />
				<GeneratePaymentButton invoice={row.original} />
				<ConfirmTransactionButton invoice={row.original} />
			</div>
		),
	},
]
